import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { useAuth } from "@/App";
import AdminLogin from "./AdminLogin";
import AdminContent from "./AdminContent";
import AdminArticles from "./AdminArticles";
import AdminCategories from "./AdminCategories";
import AdminInbox from "./AdminInbox";
import AdminSettings from "./AdminSettings";
import AdminAreas from "./AdminAreas";
import AdminDashboard from "./AdminDashboard";

const TABS = [
  { id: "visao", label: "Visão geral" },
  { id: "paginas", label: "Páginas" },
  { id: "areas", label: "Áreas de Atuação" },
  { id: "artigos", label: "Artigos" },
  { id: "categorias", label: "Categorias" },
  { id: "mensagens", label: "Mensagens" },
  { id: "curriculos", label: "Currículos" },
  { id: "config", label: "Configurações" },
];

export default function AdminApp() {
  const { user, checking, logout } = useAuth();
  const [tab, setTab] = useState("visao");

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ivory">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gold border-t-transparent" aria-label="Carregando" />
      </div>
    );
  }

  if (!user) return <AdminLogin />;

  return (
    <div className="min-h-screen bg-[#F4F2EC]">
      <Toaster position="top-right" richColors />
      <div className="flex min-h-screen">
        <aside className="hidden w-64 shrink-0 flex-col bg-navy p-7 text-ivory md:flex grain">
          <Link to="/" data-testid="admin-sidebar-logo">
            <img src="/media/logo-footer.png" alt="Silva Cotan" className="h-14 w-auto" />
          </Link>
          <nav className="mt-10 flex flex-1 flex-col gap-1" data-testid="admin-sidebar">
            {TABS.map((t) => (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`border-l-2 px-4 py-2.5 text-left text-[12px] font-semibold uppercase tracking-[0.16em] transition-colors ${
                  tab === t.id ? "border-gold text-gold" : "border-transparent text-ivory/55 hover:text-ivory"
                }`}
              >
                {t.label}
              </button>
            ))}
          </nav>
          <div className="space-y-3 border-t border-ivory/10 pt-5">
            <Link to="/" data-testid="admin-view-site" className="block text-xs text-ivory/60 transition-colors hover:text-gold">
              Ver site
            </Link>
            <button
              data-testid="admin-logout"
              onClick={() => logout().then(() => toast("Sessão encerrada."))}
              className="w-full border border-ivory/25 px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] text-ivory transition-colors hover:border-gold hover:text-gold"
            >
              Sair
            </button>
          </div>
        </aside>

        <div className="flex-1">
          <header className="flex items-center justify-between border-b border-navy/10 bg-white px-5 py-4 md:px-10">
            <div className="flex items-center gap-3 md:hidden">
              <img src="/media/logo.png" alt="Silva Cotan" className="h-10 w-auto" />
            </div>
            <p className="hidden text-[11px] font-bold uppercase tracking-[0.25em] text-gold-deep md:block">
              Silva Cotan — Área administrativa
            </p>
            <div className="flex items-center gap-3">
              <Link to="/" data-testid="admin-view-site-mobile" className="text-xs text-inkgray underline-offset-2 hover:underline md:hidden">
                Ver site
              </Link>
              <button
                data-testid="admin-logout-mobile"
                onClick={() => logout()}
                className="border border-navy/20 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-navy transition-colors hover:border-gold md:hidden"
              >
                Sair
              </button>
            </div>
          </header>

          <div className="flex gap-2 overflow-x-auto border-b border-navy/10 bg-white px-5 py-3 md:hidden">
            {TABS.map((t) => (
              <button
                key={t.id}
                data-testid={`admin-tab-mobile-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`shrink-0 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.14em] ${
                  tab === t.id ? "bg-navy text-ivory" : "border border-navy/15 text-navy/70"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <main className="p-5 md:p-10" data-testid="admin-panel">
            {tab === "visao" && <AdminDashboard go={setTab} />}
            {tab === "paginas" && <AdminContent />}
            {tab === "areas" && <AdminAreas />}
            {tab === "artigos" && <AdminArticles />}
            {tab === "categorias" && <AdminCategories />}
            {tab === "mensagens" && <AdminInbox mode="mensagens" />}
            {tab === "curriculos" && <AdminInbox mode="curriculos" />}
            {tab === "config" && <AdminSettings />}
          </main>
        </div>
      </div>
    </div>
  );
}
